import React, { useRef, useState } from 'react';
import { Spinner } from './Spinner';

/**
 * Enhanced Button Component - TrackMan Pro Design System v1.0
 *
 * Features:
 * - Seven variants: primary (gradient), secondary (outline), tertiary (ghost), ghost, success, warning, danger
 * - Three sizes: sm, md, lg
 * - Ripple effect on click
 * - Loading state with spinner
 * - Full accessibility support
 */

const createRipple = (event, buttonRef) => {
  const button = buttonRef.current;
  if (!button) return;

  const ripple = document.createElement('span');
  const rect = button.getBoundingClientRect();
  const size = Math.max(rect.width, rect.height);
  const x = event.clientX - rect.left - size / 2;
  const y = event.clientY - rect.top - size / 2;

  ripple.style.width = ripple.style.height = `${size}px`;
  ripple.style.left = `${x}px`;
  ripple.style.top = `${y}px`;
  ripple.classList.add('ripple');

  button.appendChild(ripple);

  setTimeout(() => {
    ripple.remove();
  }, 600);
};

export const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  disabled = false,
  fullWidth = false,
  icon: Icon,
  iconRight = false,
  className = '',
  onClick,
  'aria-label': ariaLabel,
  ...props
}) => {
  const buttonRef = useRef(null);
  const [minWidth, setMinWidth] = useState(null);

  // Preserve button width when loading to prevent layout shift
  const handleLoadingChange = (ref) => {
    if (ref && !minWidth) {
      setMinWidth(ref.offsetWidth);
    }
  };

  const handleClick = (e) => {
    if (!disabled && !isLoading) {
      createRipple(e, buttonRef);
      onClick?.(e);
    }
  };

  // Base styles (all buttons)
  const baseStyles = 'inline-flex items-center justify-center gap-2 font-sans font-semibold rounded-md cursor-pointer select-none transition-all duration-200 transform relative overflow-hidden focus-visible:outline-none disabled:cursor-not-allowed';

  // Size styles
  const sizeStyles = {
    sm: 'text-xs px-3 py-1 h-8',
    md: 'text-sm px-4 py-2 h-10',
    lg: 'text-base px-6 py-3 h-12'
  };

  // Variant styles
  const variantStyles = {
    primary: {
      default: 'bg-gradient-to-r from-primary-dark to-primary-light text-text-primary bg-[length:200%_100%] bg-right hover:bg-left hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:bg-surface-light disabled:text-text-muted disabled:bg-none disabled:transform-none disabled:shadow-none',
    },
    secondary: {
      default: 'bg-transparent text-text-primary border border-border hover:bg-surface-light hover:border-primary active:bg-[rgba(255,76,0,0.1)] focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:bg-transparent disabled:text-text-muted disabled:border-border-subtle',
    },
    tertiary: {
      default: 'bg-transparent text-text-secondary border-none hover:bg-surface-light hover:text-text-primary active:bg-surface focus-visible:ring-2 focus-visible:ring-text-secondary focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:text-text-muted',
    },
    ghost: {
      default: 'bg-transparent text-text-secondary border-none hover:bg-surface-light hover:text-text-primary active:bg-surface focus-visible:ring-2 focus-visible:ring-text-secondary focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:text-text-muted',
    },
    success: {
      default: 'bg-emerald-600 text-white hover:bg-emerald-500 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:bg-surface-light disabled:text-text-muted disabled:transform-none disabled:shadow-none',
    },
    warning: {
      default: 'bg-yellow-600 text-white hover:bg-yellow-500 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:bg-surface-light disabled:text-text-muted disabled:transform-none disabled:shadow-none',
    },
    danger: {
      default: 'bg-red-600 text-white hover:bg-red-500 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:bg-surface-light disabled:text-text-muted disabled:transform-none disabled:shadow-none',
    },
  };

  const widthStyle = fullWidth ? 'w-full' : '';
  const loadingStyle = isLoading ? 'opacity-75 cursor-wait' : '';

  return (
    <button
      ref={(ref) => {
        buttonRef.current = ref;
        handleLoadingChange(ref);
      }}
      className={`${baseStyles} ${sizeStyles[size]} ${variantStyles[variant].default} ${widthStyle} ${loadingStyle} ${className}`}
      disabled={disabled || isLoading}
      onClick={handleClick}
      aria-label={isLoading ? 'Loading...' : ariaLabel}
      aria-live={isLoading ? 'polite' : undefined}
      style={isLoading && minWidth ? { minWidth: `${minWidth}px` } : undefined}
      {...props}
    >
      {isLoading ? (
        <Spinner size={size === 'sm' ? 'sm' : 'md'} />
      ) : (
        <>
          {Icon && !iconRight && <Icon size={size === 'sm' ? 14 : size === 'lg' ? 20 : 16} />}
          <span>{children}</span>
          {Icon && iconRight && <Icon size={size === 'sm' ? 14 : size === 'lg' ? 20 : 16} />}
        </>
      )}
    </button>
  );
};

